// src/components/WorkflowCreator/panels/NodeConfigPanel.jsx
// Panel de configuración de nodos seleccionados

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Trash2, Info, CheckCircle, AlertCircle, ChevronDown, Loader2 } from 'lucide-react';
import { NODE_DEFINITIONS } from '../nodes/nodeDefinitions';
import { useShopifyAuth } from '../../../hooks/useShopifyAuth';
import AssistantService from '../../../services/assistant.service';
import IntegrationService from '../../../services/integration.service';
import { useAuth } from '../../../useAuth';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { scheduleToCron, cronToSchedule, describeCron } from '../../../utils/cronUtils';
import { groupLlmsByCapability } from '../../../utils/llmGrouping';

/**
 * Determina si un campo debe mostrarse según su condición `showWhen`.
 * Ej.: showWhen: { field: 'message_type', equals: 'template' } → solo visible
 * cuando otro campo del formulario tiene ese valor.
 */
const isFieldVisible = (fieldSchema, formData = {}) => {
  const cond = fieldSchema?.showWhen;
  if (!cond || !cond.field) return true;
  return formData?.[cond.field] === cond.equals;
};

/**
 * Campo de formulario genérico basado en el schema
 * Ahora soporta datos dinámicos de integraciones
 */
const FormField = ({ name, schema, value, onChange, error, integrationData = {} }) => {
  const fieldId = `field-${name}`;
  const { shopifyData, assistants, llms, loadingLLMs, integrations, loadingIntegrations } = integrationData;

  const handleChange = (newValue) => {
    onChange(name, newValue);
  };

  // Render basado en el tipo de campo
  switch (schema.type) {
    case 'text':
      // Si es shop_domain y tenemos datos de Shopify, mostrar como select
      if (name === 'shop_domain' && shopifyData?.connected && shopifyData?.shop_domain) {
        // Auto-seleccionar el shop_domain si no hay valor
        const currentValue = value || shopifyData.shop_domain;
        
        // Si el valor actual es diferente al del formulario, actualizar
        if (!value && shopifyData.shop_domain) {
          // Usar setTimeout para evitar actualizar durante el render
          setTimeout(() => handleChange(shopifyData.shop_domain), 0);
        }
        
        return (
          <div className="space-y-1">
            <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
              {schema.label}
              {schema.required && <span className="text-red-400">*</span>}
            </label>
            <div className="relative">
              <select
                id={fieldId}
                value={currentValue}
                onChange={(e) => handleChange(e.target.value)}
                className={`
                  w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
                  focus:outline-none focus:ring-1 appearance-none cursor-pointer
                  ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-purple-500/50 focus:ring-purple-500/25'}
                `}
              >
                <option value="">Select connected store...</option>
                <option value={shopifyData.shop_domain}>
                  {shopifyData.shop_name || shopifyData.shop_domain}
                </option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            <p className="text-green-400 text-xs flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Connected to Shopify
            </p>
            {error && <p className="text-red-400 text-xs">{error}</p>}
          </div>
        );
      }
      
      // Campo de texto normal
      return (
        <div className="space-y-1">
          <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>
          <input
            id={fieldId}
            type="text"
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={schema.placeholder}
            className={`
              w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
              placeholder-gray-500 focus:outline-none focus:ring-1
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-purple-500/50 focus:ring-purple-500/25'}
            `}
          />
          {name === 'shop_domain' && !shopifyData?.connected && (
            <p className="text-yellow-400 text-xs flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              No Shopify store connected. Go to Integrations to connect.
            </p>
          )}
          {schema.description && <p className="text-gray-500 text-xs">{schema.description}</p>}
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );

    case 'textarea':
      return (
        <div className="space-y-1">
          <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>
          <textarea
            id={fieldId}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={schema.placeholder}
            rows={schema.rows || 3}
            className={`
              w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
              placeholder-gray-500 focus:outline-none focus:ring-1 resize-none
              font-mono
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-purple-500/50 focus:ring-purple-500/25'}
            `}
          />
          {schema.description && <p className="text-gray-500 text-xs">{schema.description}</p>}
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );

    case 'number':
      return (
        <div className="space-y-1">
          <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>
          <input
            id={fieldId}
            type="number"
            value={value ?? schema.default ?? ''}
            onChange={(e) => handleChange(parseFloat(e.target.value))}
            min={schema.min}
            max={schema.max}
            step={schema.step || 1}
            className={`
              w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
              focus:outline-none focus:ring-1
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-purple-500/50 focus:ring-purple-500/25'}
            `}
          />
          {schema.description
            ? <p className="text-gray-500 text-xs">{schema.description}</p>
            : schema.min !== undefined && schema.max !== undefined && (
                <p className="text-gray-500 text-xs">Range: {schema.min} - {schema.max}</p>
              )
          }
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );

    case 'select':
      return (
        <div className="space-y-1">
          <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>
          <div className="relative">
            <select
              id={fieldId}
              value={value || schema.default || ''}
              onChange={(e) => handleChange(e.target.value)}
              className={`
                w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
                focus:outline-none focus:ring-1 appearance-none cursor-pointer
                ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-purple-500/50 focus:ring-purple-500/25'}
              `}
            >
              <option value="">Select...</option>
              {schema.options?.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
          {schema.description && <p className="text-gray-500 text-xs">{schema.description}</p>}
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );

    case 'json':
      return (
        <div className="space-y-1">
          <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>
          <textarea
            id={fieldId}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={schema.placeholder}
            rows={schema.rows || 4}
            className={`
              w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
              placeholder-gray-500 focus:outline-none focus:ring-1 resize-none
              font-mono
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-purple-500/50 focus:ring-purple-500/25'}
            `}
          />
          {schema.description
            ? <p className="text-gray-500 text-xs">{schema.description}</p>
            : <p className="text-gray-500 text-xs">Enter valid JSON</p>
          }
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );

    case 'assistant_select':
      // Mostrar dropdown con asistentes disponibles
      if (loadingIntegrations) {
        return (
          <div className="space-y-1">
            <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
              {schema.label}
              {schema.required && <span className="text-red-400">*</span>}
            </label>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg">
              <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
              <span className="text-gray-400 text-sm">Loading agents...</span>
            </div>
          </div>
        );
      }
      
      if (assistants && assistants.length > 0) {
        return (
          <div className="space-y-1">
            <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
              {schema.label}
              {schema.required && <span className="text-red-400">*</span>}
            </label>
            <div className="relative">
              <select
                id={fieldId}
                value={value || ''}
                onChange={(e) => handleChange(e.target.value)}
                className={`
                  w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
                  focus:outline-none focus:ring-1 appearance-none cursor-pointer
                  ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-purple-500/50 focus:ring-purple-500/25'}
                `}
              >
                <option value="">Select an agent...</option>
                {assistants.map((assistant) => (
                  <option key={assistant.id} value={assistant.id}>
                    {assistant.name} {assistant.type ? `(${assistant.type})` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            <p className="text-gray-500 text-xs">{assistants.length} agent(s) available</p>
            {error && <p className="text-red-400 text-xs">{error}</p>}
          </div>
        );
      }
      
      // Fallback: input manual si no hay asistentes
      return (
        <div className="space-y-1">
          <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>
          <input
            id={fieldId}
            type="text"
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Enter agent UUID"
            className={`
              w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
              placeholder-gray-500 focus:outline-none focus:ring-1 font-mono
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-purple-500/50 focus:ring-purple-500/25'}
            `}
          />
          <p className="text-yellow-400 text-xs flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            No agents found. Create one in the Agents section.
          </p>
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );

    case 'llm_select':
      // Dropdown con los modelos LLM disponibles (llm_call step)
      if (loadingLLMs) {
        return (
          <div className="space-y-1">
            <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
              {schema.label}
              {schema.required && <span className="text-red-400">*</span>}
            </label>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg">
              <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
              <span className="text-gray-400 text-sm">Loading models...</span>
            </div>
          </div>
        );
      }

      if (llms && llms.length > 0) {
        return (
          <div className="space-y-1">
            <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
              {schema.label}
              {schema.required && <span className="text-red-400">*</span>}
            </label>
            <div className="relative">
              <select
                id={fieldId}
                value={value || ''}
                onChange={(e) => handleChange(e.target.value)}
                className={`
                  w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
                  focus:outline-none focus:ring-1 appearance-none cursor-pointer
                  ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-purple-500/50 focus:ring-purple-500/25'}
                `}
              >
                <option value="">Select a model...</option>
                {groupLlmsByCapability(llms).map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            <p className="text-gray-500 text-xs">{llms.length} model(s) available</p>
            {error && <p className="text-red-400 text-xs">{error}</p>}
          </div>
        );
      }

      // Fallback: input manual si no hay modelos cargados
      return (
        <div className="space-y-1">
          <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>
          <input
            id={fieldId}
            type="text"
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Enter model UUID"
            className={`
              w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
              placeholder-gray-500 focus:outline-none focus:ring-1 font-mono
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-purple-500/50 focus:ring-purple-500/25'}
            `}
          />
          <p className="text-yellow-400 text-xs flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            No models available.
          </p>
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );

    case 'array':
      // Campo tipo array - para múltiples condiciones
      const items = Array.isArray(value) ? value : [];
      
      const addItem = () => {
        const newItem = {};
        // Inicializar con valores default del itemSchema
        if (schema.itemSchema) {
          Object.keys(schema.itemSchema).forEach(key => {
            newItem[key] = schema.itemSchema[key].default || '';
          });
        }
        handleChange([...items, newItem]);
      };
      
      const removeItem = (index) => {
        handleChange(items.filter((_, i) => i !== index));
      };
      
      const updateItem = (index, field, fieldValue) => {
        const updated = [...items];
        updated[index] = { ...updated[index], [field]: fieldValue };
        handleChange(updated);
      };
      
      return (
        <div className="space-y-2">
          <label className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>
          
          {/* Lista de items */}
          <div className="space-y-2">
            {items.map((item, index) => (
              <div key={index} className="bg-[#1a1a2e] border border-white/10 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-400">{schema.itemLabel || 'Condition'} {index + 1}</span>
                  <button
                    onClick={() => removeItem(index)}
                    className="text-red-400 hover:text-red-300 transition-colors"
                    title="Remove condition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Campos del item */}
                {schema.itemSchema && Object.entries(schema.itemSchema).map(([fieldName, fieldSchema]) => {
                  const fieldValue = item[fieldName] || '';
                  
                  if (fieldSchema.type === 'text') {
                    return (
                      <div key={fieldName} className="space-y-1">
                        <label className="text-xs text-gray-400">{fieldSchema.label}</label>
                        <input
                          type="text"
                          value={fieldValue}
                          onChange={(e) => updateItem(index, fieldName, e.target.value)}
                          placeholder={fieldSchema.placeholder}
                          className="w-full bg-[#0d0d1b] border border-white/5 rounded px-2 py-1.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-purple-500/50"
                        />
                      </div>
                    );
                  }
                  
                  if (fieldSchema.type === 'select') {
                    return (
                      <div key={fieldName} className="space-y-1">
                        <label className="text-xs text-gray-400">{fieldSchema.label}</label>
                        <div className="relative">
                          <select
                            value={fieldValue}
                            onChange={(e) => updateItem(index, fieldName, e.target.value)}
                            className="w-full bg-[#0d0d1b] border border-white/5 rounded px-2 py-1.5 text-white text-sm appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-purple-500/50"
                          >
                            {fieldSchema.options?.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500 pointer-events-none" />
                        </div>
                      </div>
                    );
                  }
                  
                  return null;
                })}
              </div>
            ))}
          </div>
          
          {/* Botón agregar item */}
          <button
            onClick={addItem}
            className="w-full px-3 py-2 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded-lg text-purple-400 text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            {schema.addButtonLabel || '+ Add Item'}
          </button>
          
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );

    case 'integration_select':
      // Dropdown para seleccionar integraciones disponibles
      if (loadingIntegrations) {
        return (
          <div className="space-y-1">
            <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
              {schema.label}
              {schema.required && <span className="text-red-400">*</span>}
            </label>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg">
              <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
              <span className="text-gray-400 text-sm">Loading integrations...</span>
            </div>
          </div>
        );
      }

      // Filtrar integraciones según el tipo especificado en schema
      const integrationType = schema.integration_type; // 'shopify', 'meta', 'google', 'all'
      let availableIntegrations = [];
      
      if (integrations) {
        if (!integrationType || integrationType === 'all') {
          // Mostrar todas las integraciones
          availableIntegrations = [
            ...(integrations.shopify || []).map(i => ({ ...i, type: 'shopify' })),
            ...(integrations.meta || []).map(i => ({ ...i, type: 'meta' })),
            ...(integrations.google || []).map(i => ({ ...i, type: 'google' }))
          ];
        } else {
          // Filtrar por tipo específico
          availableIntegrations = (integrations[integrationType] || []).map(i => ({ ...i, type: integrationType }));
        }
      }

      if (availableIntegrations.length > 0) {
        return (
          <div className="space-y-1">
            <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
              {schema.label}
              {schema.required && <span className="text-red-400">*</span>}
            </label>
            <div className="relative">
              <select
                id={fieldId}
                value={value || ''}
                onChange={(e) => handleChange(e.target.value)}
                className={`
                  w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
                  focus:outline-none focus:ring-1 appearance-none cursor-pointer
                  ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-purple-500/50 focus:ring-purple-500/25'}
                `}
              >
                <option value="">Select integration...</option>
                {availableIntegrations.map((integration) => {
                  const displayName = integration.shop_name || 
                                     integration.business_name || 
                                     integration.shop_domain || 
                                     integration.name || 
                                     integration.id;
                  const typeBadge = integration.type ? `[${integration.type.toUpperCase()}]` : '';
                  
                  return (
                    <option key={integration.id} value={integration.id}>
                      {typeBadge} {displayName}
                    </option>
                  );
                })}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            <p className="text-green-400 text-xs flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {availableIntegrations.length} integration(s) available
            </p>
            {error && <p className="text-red-400 text-xs">{error}</p>}
          </div>
        );
      }

      // Fallback: no hay integraciones
      return (
        <div className="space-y-1">
          <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>
          <input
            id={fieldId}
            type="text"
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Enter integration ID"
            className={`
              w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
              placeholder-gray-500 focus:outline-none focus:ring-1 font-mono
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-purple-500/50 focus:ring-purple-500/25'}
            `}
          />
          <p className="text-yellow-400 text-xs flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            No {integrationType || ''} integrations found. Go to Integrations to connect.
          </p>
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );

    case 'whatsapp_integration_select':
      // Dropdown específico para integraciones de WhatsApp
      if (loadingIntegrations) {
        return (
          <div className="space-y-1">
            <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
              {schema.label}
              {schema.required && <span className="text-red-400">*</span>}
            </label>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg">
              <Loader2 className="w-4 h-4 text-green-400 animate-spin" />
              <span className="text-gray-400 text-sm">Loading WhatsApp integrations...</span>
            </div>
          </div>
        );
      }

      // Obtener integraciones de WhatsApp/Meta
      const whatsappIntegrations = integrations?.meta || [];

      if (whatsappIntegrations.length > 0) {
        // Handler especial para auto-llenar phone_number_id
        const handleWhatsAppChange = (selectedId) => {
          // Actualizar el integration_id
          onChange(name, selectedId);
          
          // Auto-llenar business_phone_number_id si existe
          const selectedIntegration = whatsappIntegrations.find(i => i.id === selectedId);
          if (selectedIntegration?.whatsapp_info?.phone_number_id) {
            // Usar setTimeout para evitar conflictos de actualización
            setTimeout(() => {
              onChange('business_phone_number_id', selectedIntegration.whatsapp_info.phone_number_id);
            }, 0);
          }
        };

        return (
          <div className="space-y-1">
            <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
              {schema.label}
              {schema.required && <span className="text-red-400">*</span>}
            </label>
            <div className="relative">
              <select
                id={fieldId}
                value={value || ''}
                onChange={(e) => handleWhatsAppChange(e.target.value)}
                className={`
                  w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
                  focus:outline-none focus:ring-1 appearance-none cursor-pointer
                  ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-green-500/50 focus:ring-green-500/25'}
                `}
              >
                <option value="">Select WhatsApp integration...</option>
                {whatsappIntegrations.map((integration) => {
                  // Priorizar verified_name de whatsapp_info
                  const displayName = integration.whatsapp_info?.verified_name || 
                                     integration.business_name || 
                                     integration.name || 
                                     integration.whatsapp_info?.phone_number ||
                                     integration.waba_id ||
                                     integration.id;
                  
                  // Mostrar también el phone number como referencia
                  const phoneNumber = integration.whatsapp_info?.phone_number;
                  const label = phoneNumber ? `${displayName} (+${phoneNumber})` : displayName;
                  
                  return (
                    <option key={integration.id} value={integration.id}>
                      {label}
                    </option>
                  );
                })}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            {schema.description && (
              <p className="text-gray-500 text-xs">{schema.description}</p>
            )}
            <p className="text-green-400 text-xs flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {whatsappIntegrations.length} WhatsApp integration(s) available
            </p>
            {error && <p className="text-red-400 text-xs">{error}</p>}
          </div>
        );
      }

      // Fallback: no hay integraciones de WhatsApp
      return (
        <div className="space-y-1">
          <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>
          <input
            id={fieldId}
            type="text"
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Enter WhatsApp integration ID"
            className={`
              w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
              placeholder-gray-500 focus:outline-none focus:ring-1 font-mono
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-green-500/50 focus:ring-green-500/25'}
            `}
          />
          <p className="text-yellow-400 text-xs flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            No WhatsApp integrations found. Go to Integrations → Meta to connect WhatsApp Business.
          </p>
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );

    case 'instagram_integration_select':
      // Dropdown específico para integraciones de Instagram (Meta con platform instagram)
      if (loadingIntegrations) {
        return (
          <div className="space-y-1">
            <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
              {schema.label}
              {schema.required && <span className="text-red-400">*</span>}
            </label>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg">
              <Loader2 className="w-4 h-4 text-pink-400 animate-spin" />
              <span className="text-gray-400 text-sm">Loading Instagram integrations...</span>
            </div>
          </div>
        );
      }

      // Obtener integraciones Meta que tengan Instagram habilitado
      const instagramIntegrations = (integrations?.meta || []).filter(
        (i) => i.platforms_enabled?.instagram === true
      );

      if (instagramIntegrations.length > 0) {
        // Handler especial para auto-llenar instagram_business_account_id
        const handleInstagramChange = (selectedId) => {
          onChange(name, selectedId);

          const selectedIntegration = instagramIntegrations.find(i => i.id === selectedId);
          const igAccountId = selectedIntegration?.instagram_info?.account_ids?.[0];
          if (igAccountId) {
            setTimeout(() => {
              onChange('instagram_business_account_id', igAccountId);
            }, 0);
          }
        };

        return (
          <div className="space-y-1">
            <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
              {schema.label}
              {schema.required && <span className="text-red-400">*</span>}
            </label>
            <div className="relative">
              <select
                id={fieldId}
                value={value || ''}
                onChange={(e) => handleInstagramChange(e.target.value)}
                className={`
                  w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
                  focus:outline-none focus:ring-1 appearance-none cursor-pointer
                  ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-pink-500/50 focus:ring-pink-500/25'}
                `}
              >
                <option value="">Select Instagram integration...</option>
                {instagramIntegrations.map((integration) => {
                  const igInfo = integration.instagram_info || {};
                  const displayName = igInfo.username
                    ? `@${igInfo.username}`
                    : (igInfo.account_ids?.[0]
                        ? `Instagram · ${igInfo.account_ids[0]}`
                        : (integration.business_name || integration.name || integration.id));

                  return (
                    <option key={integration.id} value={integration.id}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            {schema.description && (
              <p className="text-gray-500 text-xs">{schema.description}</p>
            )}
            <p className="text-green-400 text-xs flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {instagramIntegrations.length} Instagram integration(s) available
            </p>
            {error && <p className="text-red-400 text-xs">{error}</p>}
          </div>
        );
      }

      // Fallback: no hay integraciones de Instagram
      return (
        <div className="space-y-1">
          <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>
          <input
            id={fieldId}
            type="text"
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Enter Instagram integration ID"
            className={`
              w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
              placeholder-gray-500 focus:outline-none focus:ring-1 font-mono
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-pink-500/50 focus:ring-pink-500/25'}
            `}
          />
          <p className="text-yellow-400 text-xs flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            No Instagram integrations found. Go to Integrations → Meta to connect Instagram Business.
          </p>
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );

    case 'multi_select':
      // Campo multi-select con checkboxes
      const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
      
      const toggleOption = (optionValue) => {
        const newValues = selectedValues.includes(optionValue)
          ? selectedValues.filter(v => v !== optionValue)
          : [...selectedValues, optionValue];
        handleChange(newValues);
      };

      return (
        <div className="space-y-2">
          <label className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>
          {schema.description && (
            <p className="text-gray-500 text-xs">{schema.description}</p>
          )}
          
          <div className="space-y-2 bg-[#1a1a2e] border border-white/10 rounded-lg p-3">
            {schema.options?.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              
              return (
                <label
                  key={option.value}
                  className={`
                    flex items-center gap-3 p-2 rounded cursor-pointer transition-colors
                    ${isSelected ? 'bg-green-500/10 hover:bg-green-500/15' : 'hover:bg-white/5'}
                  `}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleOption(option.value)}
                    className="w-4 h-4 rounded border-white/20 bg-[#0d0d1b] text-green-500 focus:ring-green-500/50 focus:ring-offset-0"
                  />
                  <span className={`text-sm ${isSelected ? 'text-white font-medium' : 'text-gray-300'}`}>
                    {option.label}
                  </span>
                  {option.default && !isSelected && (
                    <span className="ml-auto text-xs text-gray-500">(recommended)</span>
                  )}
                </label>
              );
            })}
          </div>
          
          {selectedValues.length > 0 && (
            <p className="text-green-400 text-xs">
              {selectedValues.length} event type(s) selected
            </p>
          )}
          
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );

    case 'schedule':
      // Campo user-friendly para configurar schedules que genera cron expressions
      const scheduleConfig = value ? cronToSchedule(value) : {
        frequency: 'day',
        minute: '0',
        hour: '9',
        dayOfWeek: '1',
        dayOfMonth: '1',
        customCron: ''
      };

      const updateSchedule = (field, fieldValue) => {
        const updated = { ...scheduleConfig, [field]: fieldValue };
        const cronExpression = scheduleToCron(updated);
        handleChange(cronExpression);
      };

      return (
        <div className="space-y-3">
          <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>

          {/* Frequency selector */}
          <div className="space-y-1">
            <label className="text-xs text-gray-400">Frequency</label>
            <div className="relative">
              <select
                value={scheduleConfig.frequency}
                onChange={(e) => updateSchedule('frequency', e.target.value)}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500/50 appearance-none cursor-pointer"
              >
                <option value="minute">Every minute</option>
                <option value="hour">Every hour</option>
                <option value="day">Every day</option>
                <option value="week">Every week</option>
                <option value="month">Every month</option>
                <option value="custom">Custom cron</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Conditional fields based on frequency */}
          {scheduleConfig.frequency === 'custom' ? (
            <div className="space-y-1">
              <label className="text-xs text-gray-400">Cron Expression</label>
              <input
                type="text"
                value={scheduleConfig.customCron}
                onChange={(e) => updateSchedule('customCron', e.target.value)}
                placeholder="0 9 * * *"
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500/50"
              />
              <p className="text-gray-500 text-xs">Format: minute hour day month day-of-week</p>
            </div>
          ) : (
            <>
              {/* Show time fields for hourly+ frequencies */}
              {scheduleConfig.frequency !== 'minute' && (
                <div className="grid grid-cols-2 gap-3">
                  {scheduleConfig.frequency !== 'hour' && (
                    <div className="space-y-1">
                      <label className="text-xs text-gray-400">Hour (0-23)</label>
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={scheduleConfig.hour}
                        onChange={(e) => updateSchedule('hour', e.target.value)}
                        className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500/50"
                      />
                    </div>
                  )}
                  <div className="space-y-1">
                    <label className="text-xs text-gray-400">Minute (0-59)</label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={scheduleConfig.minute}
                      onChange={(e) => updateSchedule('minute', e.target.value)}
                      className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500/50"
                    />
                  </div>
                </div>
              )}

              {/* Day of week for weekly */}
              {scheduleConfig.frequency === 'week' && (
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Day of Week</label>
                  <div className="relative">
                    <select
                      value={scheduleConfig.dayOfWeek}
                      onChange={(e) => updateSchedule('dayOfWeek', e.target.value)}
                      className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500/50 appearance-none cursor-pointer"
                    >
                      <option value="0">Sunday</option>
                      <option value="1">Monday</option>
                      <option value="2">Tuesday</option>
                      <option value="3">Wednesday</option>
                      <option value="4">Thursday</option>
                      <option value="5">Friday</option>
                      <option value="6">Saturday</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              )}

              {/* Day of month for monthly */}
              {scheduleConfig.frequency === 'month' && (
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Day of Month (1-31)</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={scheduleConfig.dayOfMonth}
                    onChange={(e) => updateSchedule('dayOfMonth', e.target.value)}
                    className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500/50"
                  />
                </div>
              )}
            </>
          )}

          {/* Preview */}
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg px-3 py-2">
            <p className="text-xs text-gray-400 mb-1">Preview:</p>
            <p className="text-sm text-purple-300 font-medium">{describeCron(value || scheduleToCron(scheduleConfig))}</p>
            <p className="text-xs text-gray-500 font-mono mt-1">Cron: {value || scheduleToCron(scheduleConfig)}</p>
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );

    case 'whatsapp_button_url': {
      // Campo amigable (no JSON crudo) para el botón URL dinámico de una plantilla.
      // El usuario solo escribe el sufijo que reemplaza el {{1}} del botón.
      // El valor en formData puede ser un array (step cargado del backend, passthrough)
      // o un string (editado aquí); generateStepParams normaliza a array al guardar.
      const buttonSuffix = Array.isArray(value)
        ? (value[0]?.value ?? '')
        : (typeof value === 'string' ? value : '');
      return (
        <div className="space-y-1">
          <label htmlFor={fieldId} className="text-sm text-gray-300 flex items-center gap-1">
            {schema.label}
            {schema.required && <span className="text-red-400">*</span>}
          </label>
          <input
            id={fieldId}
            type="text"
            value={buttonSuffix}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={schema.placeholder}
            className={`
              w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
              placeholder-gray-500 focus:outline-none focus:ring-1 font-mono
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-green-500/50 focus:ring-green-500/25'}
            `}
          />
          {schema.description && <p className="text-gray-500 text-xs">{schema.description}</p>}
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      );
    }

    default:
      return (
        <div className="text-gray-500 text-sm">
          Unknown field type: {schema.type}
        </div>
      );
  }
};

/**
 * Panel de configuración de nodo
 */
const NodeConfigPanel = ({
  selectedNode,
  onClose,
  onSave,
  onDelete,
  isOpen = true
}) => {
  const [formData, setFormData] = useState({});
  const [stepName, setStepName] = useState('');
  const [stepDescription, setStepDescription] = useState('');
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  
  // Integration data states
  const [assistants, setAssistants] = useState([]);
  const [llms, setLLMs] = useState([]);
  const [loadingLLMs, setLoadingLLMs] = useState(false);
  const [integrations, setIntegrations] = useState(null);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  
  // Hooks para obtener datos de usuario e integraciones
  const { user } = useAuth();
  const { currentAccount } = useOrganization();
  const { isConnected: shopifyConnected, shopDomain, shopData, integrationId: shopifyIntegrationId } = useShopifyAuth();

  // Obtener la definición del nodo
  const nodeDefinition = selectedNode?.data?.type
    ? NODE_DEFINITIONS[selectedNode.data.type]
    : null;

  // Cargar asistentes e integraciones cuando se abre el panel
  useEffect(() => {
    const loadData = async () => {
      if (!user?.user_id || !currentAccount?.id) {
        console.log('🤖 [NodeConfig] No user ID or account ID available');
        return;
      }
      
      // Verificar qué datos necesita cargar (incluyendo campos advanced)
      const allSchemaFields = [
        ...Object.values(nodeDefinition?.parameters?.schema || {}),
        ...Object.values(nodeDefinition?.parameters?.advanced || {})
      ];

      const needsAssistants = allSchemaFields.some(s => s.type === 'assistant_select');
      const needsLLMs = allSchemaFields.some(s => s.type === 'llm_select');
      const needsIntegrations = allSchemaFields.some(s =>
        s.type === 'integration_select' ||
        s.type === 'whatsapp_integration_select' ||
        s.type === 'instagram_integration_select'
      );

      if (!needsAssistants && !needsLLMs && !needsIntegrations) return;

      console.log('🤖 [NodeConfig] Loading data for user:', user.user_id, 'account:', currentAccount.id);
      setLoadingIntegrations(true);

      try {
        // Cargar asistentes si se necesita
        if (needsAssistants) {
          const result = await AssistantService.listAssistants({
            ownerId: user.user_id,
            limit: 50
          });

          if (result.success && result.data) {
            setAssistants(result.data);
            console.log('🤖 [NodeConfig] Loaded assistants:', result.data.length);
          }
        }

        // Cargar modelos LLM si se necesita (llm_call step)
        if (needsLLMs) {
          setLoadingLLMs(true);
          try {
            const result = await AssistantService.listLLMs(true);
            if (result.success && result.data) {
              setLLMs(result.data);
              console.log('🧠 [NodeConfig] Loaded LLMs:', result.data.length);
            }
          } finally {
            setLoadingLLMs(false);
          }
        }

        // Cargar integraciones si se necesita
        if (needsIntegrations) {
          const result = await IntegrationService.listAllIntegrations(currentAccount.id);
          
          if (result.success && result.data) {
            setIntegrations(result.data);
            console.log('🔌 [NodeConfig] Loaded integrations:', {
              shopify: result.data.shopify?.length || 0,
              meta: result.data.meta?.length || 0,
              google: result.data.google?.length || 0
            });
          }
        }
      } catch (err) {
        console.error('🤖 [NodeConfig] Error loading data:', err);
      } finally {
        setLoadingIntegrations(false);
      }
    };
    
    loadData();
  }, [user?.user_id, currentAccount?.id, nodeDefinition]);

  // Auto-fill shop_domain e integration_id si es un Shopify trigger y está conectado (legacy)
  useEffect(() => {
    if (nodeDefinition?.type === 'shopify_trigger' && shopifyConnected && shopDomain) {
      const updates = {};
      
      if (!formData.shop_domain) {
        updates.shop_domain = shopDomain;
      }
      
      if (!formData.integration_id && shopifyIntegrationId) {
        updates.integration_id = shopifyIntegrationId;
      }
      
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({
          ...prev,
          ...updates
        }));
        setHasChanges(true); // Marcar que hay cambios para habilitar el botón Save
      }
    }
  }, [nodeDefinition?.type, shopifyConnected, shopDomain, shopifyIntegrationId]);

  // Datos de integración para pasar a FormField
  const integrationData = {
    shopifyData: {
      connected: shopifyConnected,
      shop_domain: shopDomain,
      shop_name: shopData?.name,
      integration_id: shopifyIntegrationId
    },
    assistants,
    llms,
    loadingLLMs,
    integrations,
    loadingIntegrations
  };

  // Inicializar formulario cuando cambia el nodo
  useEffect(() => {
    if (selectedNode?.data) {
      setFormData(selectedNode.data.params || {});
      setStepName(selectedNode.data.stepName || '');
      setStepDescription(selectedNode.data.stepDescription || '');
      setErrors({});
      setHasChanges(false);
      setAdvancedOpen(false);
    }
  }, [selectedNode?.id]);

  const handleFieldChange = useCallback((name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setHasChanges(true);
    
    // Auto-llenar phone_number_id cuando se selecciona una integración de Meta
    if (name === 'integration_id' && value && nodeDefinition?.type === 'whatsapp_notification') {
      const fetchPhoneNumberId = async () => {
        try {
          const result = await IntegrationService.getIntegrationDetails(value, 'meta');
          if (result.success && result.data?.whatsapp_info?.phone_number_id) {
            setFormData(prev => ({
              ...prev,
              phone_number_id: result.data.whatsapp_info.phone_number_id
            }));
          }
        } catch (err) {
          console.error('Error fetching integration details:', err);
        }
      };
      fetchPhoneNumberId();
    }
    
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, nodeDefinition]);

  const validateForm = useCallback(() => {
    if (!nodeDefinition?.parameters?.schema) return true;

    const newErrors = {};
    const { required = [], schema } = nodeDefinition.parameters;

    // Validar campos requeridos (omitir los ocultos por showWhen)
    required.forEach(fieldName => {
      const fieldSchema = schema[fieldName];
      if (fieldSchema && !isFieldVisible(fieldSchema, formData)) return;
      const value = formData[fieldName];
      const isEmpty = value === undefined || value === null || value === '' ||
        (Array.isArray(value) && value.length === 0);
      if (isEmpty) {
        newErrors[fieldName] = 'This field is required';
      }
    });

    // Validar campos JSON (omitir los ocultos por showWhen)
    Object.entries(schema).forEach(([fieldName, fieldSchema]) => {
      if (!isFieldVisible(fieldSchema, formData)) return;
      if (fieldSchema.type === 'json' && formData[fieldName]) {
        try {
          JSON.parse(formData[fieldName]);
        } catch (e) {
          newErrors[fieldName] = 'Invalid JSON format';
        }
      }
    });

    // Validar step name (requerido para todos los nodos que no son triggers)
    if (!nodeDefinition.isTrigger && !stepName.trim()) {
      newErrors._stepName = 'Step name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, nodeDefinition, stepName]);

  const handleSave = useCallback(() => {
    if (!validateForm()) return;

    onSave({
      nodeId: selectedNode.id,
      params: formData,
      stepName: stepName.trim(),
      stepDescription: stepDescription.trim(),
      isConfigured: true
    });
    setHasChanges(false);
  }, [formData, stepName, stepDescription, selectedNode, onSave, validateForm]);

  if (!isOpen || !selectedNode || !nodeDefinition) {
    return null;
  }

  const { parameters } = nodeDefinition;
  const paramSchema = parameters?.schema || {};

  return (
    <motion.div
      initial={{ x: 300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 300, opacity: 0 }}
      className="h-full flex flex-col bg-[#0d0d0d] border-l border-white/10"
    >
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-white font-semibold text-lg">Configure Node</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        
        {/* Node type badge */}
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r ${nodeDefinition.color} bg-opacity-20`}>
          <span className="text-white text-sm font-medium">{nodeDefinition.label}</span>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
        {/* Step name (required for non-trigger nodes) */}
        {!nodeDefinition.isTrigger && (
          <div className="space-y-1">
            <label className="text-sm text-gray-300 flex items-center gap-1">
              Step Name
              <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={stepName}
              onChange={(e) => {
                setStepName(e.target.value);
                setHasChanges(true);
              }}
              placeholder="e.g., Analyze Order"
              className={`
                w-full bg-[#1a1a2e] border rounded-lg px-3 py-2 text-white text-sm
                placeholder-gray-500 focus:outline-none focus:ring-1
                ${errors._stepName ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25' : 'border-white/10 focus:border-purple-500/50 focus:ring-purple-500/25'}
              `}
            />
            {errors._stepName && <p className="text-red-400 text-xs">{errors._stepName}</p>}
            <p className="text-gray-500 text-xs">Used to reference this step in templates</p>
          </div>
        )}

        {/* Step description (optional) */}
        <div className="space-y-1">
          <label className="text-sm text-gray-300">Description</label>
          <textarea
            value={stepDescription}
            onChange={(e) => {
              setStepDescription(e.target.value);
              setHasChanges(true);
            }}
            placeholder="Optional description of what this step does..."
            rows={2}
            className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/25 resize-none"
          />
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 pt-4">
          <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">
            Parameters
          </h3>
        </div>

        {/* Dynamic form fields */}
        {Object.entries(paramSchema)
          .filter(([, fieldSchema]) => isFieldVisible(fieldSchema, formData))
          .map(([fieldName, fieldSchema]) => (
            <FormField
              key={fieldName}
              name={fieldName}
              schema={fieldSchema}
              value={formData[fieldName]}
              onChange={handleFieldChange}
              error={errors[fieldName]}
              integrationData={integrationData}
            />
          ))}

        {/* Template variables help */}
        <div className="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg mt-4">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-indigo-300 text-xs font-medium">Template Variables</p>
              <p className="text-indigo-300/70 text-xs mt-1">
                Use <code className="bg-black/20 px-1 rounded">{'{{input:webhook_payload.field}}'}</code> for trigger data
                or <code className="bg-black/20 px-1 rounded">{'{{context:StepName.result_data}}'}</code> for previous step results.
              </p>
            </div>
          </div>
        </div>

        {/* Advanced Configuration */}
        {Object.keys(nodeDefinition.parameters?.advanced || {}).length > 0 && (
          <div className="border-t border-white/10 pt-4 mt-2">
            <button
              type="button"
              onClick={() => setAdvancedOpen(prev => !prev)}
              className="flex items-center justify-between w-full text-left group"
            >
              <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider group-hover:text-gray-300 transition-colors">
                Advanced Configuration
              </h3>
              <ChevronDown
                className={`w-4 h-4 text-gray-500 group-hover:text-gray-400 transition-all ${advancedOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {advancedOpen && (
              <div className="space-y-4 mt-3">
                {Object.entries(nodeDefinition.parameters.advanced)
                  .filter(([, fieldSchema]) => isFieldVisible(fieldSchema, formData))
                  .map(([fieldName, fieldSchema]) => (
                    <FormField
                      key={fieldName}
                      name={fieldName}
                      schema={fieldSchema}
                      value={formData[fieldName]}
                      onChange={handleFieldChange}
                      error={errors[fieldName]}
                      integrationData={integrationData}
                    />
                  ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-white/10 space-y-2">
        {/* Save button */}
        <button
          onClick={handleSave}
          disabled={!hasChanges}
          className={`
            w-full flex items-center justify-center gap-2 py-2 rounded-lg font-medium text-sm
            transition-all duration-200
            ${hasChanges
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          <Save className="w-4 h-4" />
          Save Configuration
        </button>

        {/* Delete button */}
        <button
          onClick={() => onDelete(selectedNode.id)}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg font-medium text-sm text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Delete Node
        </button>
      </div>
    </motion.div>
  );
};

export default NodeConfigPanel;
